if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        if (!this.form) return;

        this.submitButton = this.querySelector('[type="submit"]');
        if (!this.submitButton) return;

        // Prefer your label span if present (matches your buy-buttons.liquid)
        this.submitButtonText =
          this.submitButton.querySelector('.cbh-atc__label') ||
          this.submitButton.querySelector('span') ||
          this.submitButton;

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Ensure there is exactly ONE hidden variant id input, and it is writable
        this.ensureSingleVariantIdInput();

        // Allow ProductInfo to update the hidden id input
        if (this.variantIdInput) this.variantIdInput.disabled = false;

        // Prefer cart drawer (and re-resolve at submit time)
        this.cart = null;
        this.resolveCart();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        // Internal submit lock + abort handling
        this._cbhSubmitting = false;
        this._cbhAbortController = null;

        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      /* ---------------------------
        Cart target (drawer preferred)
      ---------------------------- */

      resolveCart() {
        // Prefer drawer first (you want drawer behaviour)
        this.cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        return this.cart;
      }

      isCartDrawer() {
        return !!(this.cart && this.cart.tagName && this.cart.tagName.toLowerCase() === 'cart-drawer');
      }

      openCartDrawer(triggeredBy = null) {
        if (!this.cart || !this.isCartDrawer()) return;

        try {
          // Ensure a11y + focus return
          if (triggeredBy) this.cart.setActiveElement(triggeredBy);

          // Open the drawer (your cart-drawer has open())
          if (typeof this.cart.open === 'function') {
            this.cart.open(triggeredBy || this.submitButton);
          } else {
            // Fallback: add active class if open() is missing for any reason
            this.cart.classList.add('active');
          }
        } catch (e) {}
      }

      /* ---------------------------
        Reliability helpers
      ---------------------------- */

      ensureSingleVariantIdInput() {
        if (!this.form) return;

        const inputs = Array.from(this.form.querySelectorAll('input[name="id"]'));
        let keeper = inputs[0];

        if (!keeper) {
          keeper = document.createElement('input');
          keeper.type = 'hidden';
          keeper.name = 'id';
          this.form.prepend(keeper);
        }

        // Remove duplicates
        inputs.slice(1).forEach((i) => i.remove());

        // Ensure it’s enabled so scripts can update it
        keeper.disabled = false;

        // Keep a stable reference
        this._cbhVariantIdInput = keeper;
      }

      getVariantId() {
        const input = this.variantIdInput;
        const id = input ? String(input.value || '').trim() : '';
        return id || null;
      }

      canSubmit() {
        if (!this.form || !this.submitButton) return false;
        if (this._cbhSubmitting) return false;

        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.hasAttribute('disabled')) {
          return false;
        }
        return true;
      }

      /* ---------------------------
        Emit state changes
      ---------------------------- */

      emitState(detail = {}) {
        try {
          this.dispatchEvent(
            new CustomEvent('product-form:state', {
              bubbles: true,
              detail: {
                sectionId: this.closest('product-info')?.dataset.section || null,
                formId: this.form?.id || null,
                inSticky: !!this.closest('.sticky-atc'),
                variantId: this.variantIdInput?.value || null,
                ...detail,
              },
            })
          );
        } catch (e) {}
      }

      setLoading(isLoading) {
        if (!this.submitButton) return;

        const spinner = this.querySelector('.loading__spinner');

        if (isLoading) {
          this.submitButton.setAttribute('aria-disabled', 'true');
          this.submitButton.classList.add('loading');
          if (spinner) spinner.classList.remove('hidden');
        } else {
          this.submitButton.classList.remove('loading');
          if (spinner) spinner.classList.add('hidden');

          // Only clear aria-disabled if not disabled for real stock reasons.
          if (!this.submitButton.hasAttribute('disabled')) {
            this.submitButton.removeAttribute('aria-disabled');
          }
        }

        this.emitState({
          loading: !!isLoading,
          ariaDisabled: this.submitButton.getAttribute('aria-disabled') === 'true',
          disabled: this.submitButton.hasAttribute('disabled'),
          text: (this.submitButtonText?.textContent || '').trim(),
        });
      }

      /* ---------------------------
        Error normaliser (cart add)
      ---------------------------- */

      normaliseCartAddError(err) {
        try {
          if (!err) return '';
          if (typeof err === 'string') return err;
          if (Array.isArray(err)) return err.filter(Boolean).join(' ');
          if (typeof err === 'object') {
            const parts = [];
            for (const k in err) {
              const v = err[k];
              if (Array.isArray(v)) parts.push(...v);
              else if (typeof v === 'string') parts.push(v);
            }
            return parts.filter(Boolean).join(' ');
          }
          return '';
        } catch (e) {
          return '';
        }
      }

      /* ---------------------------
        Submit
      ---------------------------- */

      onSubmitHandler(evt) {
        evt.preventDefault();
        evt.stopPropagation();
        evt.stopImmediatePropagation();

        if (!this.canSubmit()) return;

        // Re-resolve cart at submit time (prevents “drawer not in DOM yet” issues)
        this.resolveCart();

        // Make sure we still have one id input (sections can re-render)
        this.ensureSingleVariantIdInput();

        this.handleErrorMessage();

        const variantId = this.getVariantId();
        if (!variantId) {
          this.handleErrorMessage(window.variantStrings?.unavailable || 'Please select an option.');
          this.emitState({ submitted: true, success: false, error: true, message: 'Missing variant id' });
          return;
        }

        this.error = false;
        this._cbhSubmitting = true;
        this.setLoading(true);

        // Abort any previous pending request for this component
        try {
          this._cbhAbortController?.abort?.();
        } catch (e) {}
        this._cbhAbortController = window.AbortController ? new AbortController() : null;

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        formData.set('id', variantId);

        if (this.cart) {
          // cart-drawer + cart-notification both use .getSectionsToRender().map(s => s.id)
          try {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          } catch (e) {}
        }

        config.body = formData;
        if (this._cbhAbortController) config.signal = this._cbhAbortController.signal;

        const cartAddUrl =
  (window.routes && window.routes.cart_add_url) ||
  (typeof routes !== 'undefined' && routes && routes.cart_add_url) ||
  '/cart/add';

fetch(cartAddUrl, config)
          .then((response) => response.json())
          .then((response) => {
            // Shopify “error” shape
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });

              const msg =
                response.description ||
                response.message ||
                this.normaliseCartAddError(response.errors) ||
                window.cartStrings?.error ||
                'Something went wrong. Please try again.';

              this.handleErrorMessage(msg);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (soldOutMessage) {
  this.submitButton.setAttribute('disabled', 'disabled'); // ✅ makes it a real disabled state
  this.submitButton.setAttribute('aria-disabled', 'true');
  if (this.submitButtonText) this.submitButtonText.classList.add('hidden');
  soldOutMessage.classList.remove('hidden');
}

              this.error = true;
              this.emitState({ submitted: true, success: false, error: true, message: msg });
              return;
            }

            // If no cart UI exists, fall back to cart page
            if (!this.cart) {
              const cartUrl =
  (window.routes && window.routes.cart_url) ||
  (typeof routes !== 'undefined' && routes && routes.cart_url) ||
  '/cart';

window.location = cartUrl;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            });

            this.error = false;

            const renderAndOpen = () => {
              try {
                this.cart.renderContents(response);
              } catch (e) {}

              // KEY FIX: cart-drawer does NOT auto-open on renderContents
              // We want clear user feedback, so always open the drawer after a successful add.
              this.openCartDrawer(this.submitButton);
            };

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    renderAndOpen();
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              renderAndOpen();
            }

            this.emitState({ submitted: true, success: true, error: false });
          })
          .catch((e) => {
            if (e && e.name === 'AbortError') return;

            console.error(e);
            this.error = true;

            this.emitState({
              submitted: true,
              success: false,
              error: true,
              message: e && e.message ? e.message : 'Request failed',
            });

            if (!this.hideErrors) {
              this.handleErrorMessage(window.cartStrings?.error || 'Something went wrong. Please try again.');
            }
          })
          .finally(() => {
            this._cbhSubmitting = false;

            // Header cart total/count refresh — debounced + safe
            ProductForm.refreshHeaderCartMeta();

            this.setLoading(false);

            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');

            // If not error, re-enable aria-disabled (but NOT disabled attribute)
            if (!this.error && this.submitButton) {
              if (!this.submitButton.hasAttribute('disabled')) {
                this.submitButton.removeAttribute('aria-disabled');
              }
            }
          });
      }

      /* ---------------------------
        Errors + toggle
      ---------------------------- */

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');

        if (!this.errorMessageWrapper) return;

        this.errorMessage =
          this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        // Show/hide
        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        // Standardised styling hook
        if (errorMessage) {
          this.errorMessageWrapper.classList.add('cart-error');
          this.errorMessage.textContent = errorMessage;
        } else {
          this.errorMessageWrapper.classList.remove('cart-error');
          this.errorMessage.textContent = '';
        }
      }

      toggleSubmitButton(disable = true, text) {
  if (!this.submitButton) return;

  const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
  const defaultText = (window.variantStrings && window.variantStrings.addToCart) || 'Add to basket';

  if (disable) {
    this.submitButton.setAttribute('disabled', 'disabled');
    this.submitButton.setAttribute('aria-disabled', 'true');
    if (text && this.submitButtonText) this.submitButtonText.textContent = text;
  } else {
    this.submitButton.removeAttribute('disabled');
    this.submitButton.removeAttribute('aria-disabled');

    // ✅ Restore label visibility if it was hidden by a prior sold-out/error state
    if (this.submitButtonText) this.submitButtonText.classList.remove('hidden');
    if (soldOutMessage) soldOutMessage.classList.add('hidden');

    if (this.submitButtonText) this.submitButtonText.textContent = defaultText;
  }

  this.emitState({
    toggled: true,
    disabled: this.submitButton.hasAttribute('disabled'),
    ariaDisabled: this.submitButton.getAttribute('aria-disabled') === 'true',
    text: (this.submitButtonText?.textContent || '').trim(),
  });
}

      /* ---------------------------
        Header cart meta refresh (safe)
      ---------------------------- */

      static refreshHeaderCartMeta() {
        if (window.__CBH_HEADER_CART_META_TIMER__) {
          clearTimeout(window.__CBH_HEADER_CART_META_TIMER__);
        }

        window.__CBH_HEADER_CART_META_TIMER__ = setTimeout(() => {
          const root = (window.routes && window.routes.root_url) || '/';
          fetch(`${root}?section_id=header`)
            .then((res) => res.text())
            .then((html) => {
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const newTotal = doc.querySelector('.carttotal');
              const newCount = doc.querySelector('.cartcount');
              const curTotal = document.querySelector('.carttotal');
              const curCount = document.querySelector('.cartcount');

              if (newTotal && curTotal) curTotal.innerHTML = newTotal.innerHTML;
              if (newCount && curCount) curCount.innerHTML = newCount.innerHTML;
            })
            .catch(() => {});
        }, 120);
      }

      /* ---------------------------
        Getters
      ---------------------------- */

      get variantIdInput() {
        if (this._cbhVariantIdInput) return this._cbhVariantIdInput;
        return this.form?.querySelector('[name=id]');
      }
    }
  );
}
